import axiosInstance from '../../axios/axiosInstance'
import { apiErrorBody, apiErrorText } from '../../i18n/apiError'

/** A segmented word, as ZhWords renders it. */
export interface ZhToken {
  word: string
  pinyin?: string
  english?: string
}

export interface DiscussionReply {
  id: string
  name: string
  isTeacher: boolean
  mine: boolean
  text: string
  content: ZhToken[]
  createdAt: string
  likes: number
  /** The viewer liked it. */
  liked: boolean
  /** Moderators only: other learners don't see it. */
  hidden?: boolean
  hiddenReason?: string
}

export interface DiscussionAnswer {
  ref: string
  name: string
  isTeacher: boolean
  mine: boolean
  text: string
  content: ZhToken[]
  likes: number
  liked: boolean
  replies: DiscussionReply[]
  hidden?: boolean
  hiddenReason?: string
}

export interface DiscussionUnread {
  id: string
  answerRef: string
  actorName: string
  actorIsTeacher: boolean
}

export interface Discussion {
  storyId: string
  title: string
  englishTitle: string
  question: string
  questionContent: ZhToken[]
  /** Other learners' answers the viewer can see. */
  total: number
  canModerate: boolean
  /** The viewer's own answer first, then active learners, then everyone else. */
  answers: DiscussionAnswer[]
  /** Replies to the viewer's answer they haven't seen yet. */
  unread: DiscussionUnread[]
}

export type DiscussionTargetKind = 'answer' | 'reply'

export type NotificationKind =
  | 'reply_to_answer'
  | 'teacher_reply'
  | 'reply_in_thread'
  | 'words_added'
  | 'answer_liked'
  | 'reply_liked'
  | 'correction_ready'

export interface DiscussionNotification {
  id: string
  kind: NotificationKind
  storyId: string
  storyTitle: string
  answerRef: string
  replyId: string | null
  actorName: string
  actorIsTeacher: boolean
  preview: string
  count?: number
  read: boolean
  at: string
}

/** The server's reason in the interface language (errors:<code>, else its message), else a fallback. */
export const errorMessage = (error: any, fallback: string): string => apiErrorText(error) ?? fallback

/** The server's error code, e.g. DISCUSSION_LESSON_GONE (src/common/error-codes.ts in the backend). */
export const errorCode = (error: any): string | undefined => {
  const code = apiErrorBody(error)?.code
  return typeof code === 'string' ? code : undefined
}

export const fetchDiscussion = (storyId: string): Promise<Discussion> =>
  axiosInstance.get(`discussion/${encodeURIComponent(storyId)}`).then((res) => res.data)

export const postReply = (answerRef: string, text: string): Promise<DiscussionReply> =>
  axiosInstance.post(`discussion/answers/${encodeURIComponent(answerRef)}/replies`, { text }).then((res) => res.data.reply)

export const deleteReply = (id: string) => axiosInstance.delete(`discussion/replies/${encodeURIComponent(id)}`)

export const likeItem = (kind: DiscussionTargetKind, id: string, liked: boolean): Promise<{ liked: boolean; likes: number }> =>
  axiosInstance.post('discussion/like', { kind, id, liked }).then((res) => res.data)

export const reportItem = (kind: DiscussionTargetKind, id: string) => axiosInstance.post('discussion/report', { kind, id })

export const blockAuthor = (kind: DiscussionTargetKind, id: string) => axiosInstance.post('discussion/block', { kind, id })

export const moderateItem = (kind: DiscussionTargetKind, id: string, visible: boolean) =>
  axiosInstance.post('discussion/moderate', { kind, id, visible })

/** Tells the answer's author someone looked a word up in it. Best effort: never surfaces an error. */
export const wordAdded = (answerRef: string) =>
  axiosInstance.post(`discussion/answers/${encodeURIComponent(answerRef)}/word-added`).catch(() => undefined)

export const fetchNotifications = (): Promise<{ unreadCount: number; notifications: DiscussionNotification[] }> =>
  axiosInstance.get('notifications').then((res) => res.data)

/** Marks the given notifications read, or all of them, or (with `kind`) all of that kind. */
export const markNotificationsRead = (ids?: string[], kind?: NotificationKind) =>
  axiosInstance
    .post('notifications/read', { ...(ids && ids.length ? { ids } : {}), ...(kind ? { kind } : {}) })
    .catch(() => undefined)

export const fetchDiscussionSettings = (): Promise<{ replyEmails: boolean }> =>
  axiosInstance.get('discussion/settings').then((res) => res.data)

export const saveDiscussionSettings = (replyEmails: boolean): Promise<{ replyEmails: boolean }> =>
  axiosInstance.put('discussion/settings', { replyEmails }).then((res) => res.data)
